import {reduce} from "lodash";
import {useState, useRef, RefObject} from "react";
import {Actions, MenuRef, ControlRef, State, DefaultStateProps} from "./types";

type InputProps = DefaultStateProps &
  Partial<Actions> &
  Partial<Omit<State, "focus">>;

type ManagedProps = State &
  Actions & {
    menuRef: RefObject<MenuRef>;
    controlRef: RefObject<ControlRef>;
  };

const getInitialState = ({
  defaultMenuIsOpen,
  defaultValue,
  defaultHighlightedIndex,
  menuIsOpen = defaultMenuIsOpen || false,
  value = defaultValue,
  highlightedIndex = defaultHighlightedIndex || 0,
}: InputProps): State => ({
  menuIsOpen,
  value,
  highlightedIndex,
  focus: false,
});

const focusRef = (ref: React.RefObject<HTMLElement>): void =>
  ref?.current?.focus();

const getState = (state: State, props: InputProps): State =>
  reduce(
    state,
    (newState, stateValue, stateKey) => {
      const propValue = props[stateKey as keyof InputProps];
      return {
        ...newState,
        [stateKey]: propValue ?? stateValue,
      };
    },
    {}
  ) as State;

const useStateManager = (props: InputProps): ManagedProps => {
  const initialState = getInitialState(props);
  const [value, setValue] = useState(initialState.value);
  const [menuIsOpen, setMenuIsOpen] = useState(initialState.menuIsOpen);
  const [focus, setFocus] = useState(initialState.focus);
  const [highlightedIndex, setHighlightedIndex] = useState(
    initialState.highlightedIndex
  );
  const controlRef = useRef<ControlRef>(null);
  const menuRef = useRef<MenuRef>(null);

  const onBlur: Actions["onBlur"] = (e) => {
    setFocus(false);
    props.onBlur?.(e);
  };

  const onFocus: Actions["onFocus"] = (e) => {
    setFocus(true);
    props.onFocus?.(e);
  };

  const onMenuOpen: Actions["onMenuOpen"] = () => {
    setMenuIsOpen(true);
    focusRef(menuRef);
  };

  const onMenuClose: Actions["onMenuClose"] = () => {
    setMenuIsOpen(false);
    focusRef(controlRef);
  };

  const onChange: Actions["onChange"] = (option, highlightedIndex) => {
    setValue(option);
    onMenuClose();
    props.onChange?.(option, highlightedIndex);
  };

  const onOptionHighlighted: Actions["onOptionHighlighted"] = (index) => {
    setHighlightedIndex(index);
    props.onOptionHighlighted?.(index);
  };

  const reset: Actions["reset"] = () => {
    props.reset?.();
    setHighlightedIndex(initialState.highlightedIndex);
    setMenuIsOpen(initialState.menuIsOpen);
    setFocus(initialState.focus);
  };

  const state = {
    value,
    menuIsOpen,
    focus,
    highlightedIndex,
  };

  return {
    ...getState(state, props),
    controlRef,
    menuRef,
    onBlur,
    onFocus,
    onMenuOpen,
    onMenuClose,
    onChange,
    onOptionHighlighted,
    reset,
  };
};

export default useStateManager;
